import type { VacationBalance } from '../../services/api/timeOff';
import {
  formatBalanceHours,
  formatDaysAndHours,
  hasVacationEntitlement,
} from '../../utils/vacationBalance';

interface VacationBalanceCardProps {
  balance: VacationBalance | null | undefined;
  isLoading?: boolean;
}

/**
 * MKS-326 — the employee's own vacation balance on /time-off.
 *
 * Every figure is shown in the person's OWN working days as well as hours: at
 * 0.5 FTE a day is 4 h, so "20 h" is 5 days, not 2.5. Printing only hours (or
 * only notional 8 h days) is what made part-timers read their balance as half
 * of what it is.
 *
 * Renders NOTHING when the person has no entitlement — a contractor or
 * freelancer gets an all-null balance back, and an empty card would imply they
 * have run out rather than that the concept does not apply to them.
 *
 * `pendingHours` is listed beside `remainingHours`, not subtracted from it:
 * remaining keeps its established "already approved" meaning, and the reader
 * is told separately how much is still awaiting a manager.
 */
export function VacationBalanceCard({ balance, isLoading }: VacationBalanceCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <p className="text-sm text-gray-500">Loading vacation balance…</p>
      </div>
    );
  }

  if (!hasVacationEntitlement(balance)) return null;

  const { year, entitlementHours, carriedInHours, usedHours, pendingHours, remainingHours } =
    balance;
  const dayHours = balance.dailyWorkingHours;

  return (
    <section
      aria-label="Vacation balance"
      className="bg-white rounded-lg border border-gray-200 p-6"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">Vacation balance {year}</h2>
        <span className="text-xs text-gray-500">
          Your working day is {formatBalanceHours(dayHours)} h
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <Figure label="Entitlement" hours={entitlementHours} dayHours={dayHours} />
        <Figure label="Carried in" hours={carriedInHours} dayHours={dayHours} />
        <Figure label="Used" hours={usedHours} dayHours={dayHours} />
        <Figure label="Pending" hours={pendingHours} dayHours={dayHours} />
        <Figure label="Remaining" hours={remainingHours} dayHours={dayHours} emphasis />
      </dl>

      {pendingHours > 0 && (
        <p className="mt-4 text-sm text-gray-500">
          {formatDaysAndHours(pendingHours, dayHours)} is awaiting approval —{' '}
          {formatDaysAndHours(remainingHours - pendingHours, dayHours)} left if it is all approved.
        </p>
      )}

      {remainingHours < 0 && (
        <p className="mt-4 text-sm text-amber-700">
          You have taken more vacation than this year&apos;s entitlement. The excess carries into
          next year.
        </p>
      )}

      <p className="mt-4 text-xs text-gray-400">
        Approved vacation counts as used even when the dates are still ahead of you.
      </p>
    </section>
  );
}

function Figure({
  label,
  hours,
  dayHours,
  emphasis,
}: {
  label: string;
  hours: number;
  dayHours: number;
  emphasis?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={`mt-1 tabular-nums ${emphasis ? 'text-xl font-semibold' : 'text-lg'} ${
          hours < 0 ? 'text-red-600' : 'text-gray-900'
        }`}
      >
        {formatDaysAndHours(hours, dayHours)}
      </dd>
    </div>
  );
}
